import React, { useState } from "react";
import { Alert, Button, Statistic } from "antd";
import { FormatterUtils } from "@nubeteck/utils";
import { useVerificarPagoBacSimplePayMutation } from "src/services/cartServices";
import { ActionButtons, CountdownRow, ManualTransferBox } from "./bac-simple-pay-form.styled";

interface BacManualTransferStepProps {
  content?: Record<string, string | undefined> | null;
  preregisterId: string;
  totalAmount: number;
  currencySymbol: string;
  orgPhone?: string;
  expiresAt: number;
  onExpire: () => void;
  onCancel: () => void;
  onSuccess: (pagoId: number) => void;
  onRestart: () => void;
}

function BacManualTransferStep({
  content,
  preregisterId,
  totalAmount,
  currencySymbol,
  orgPhone,
  expiresAt,
  onExpire,
  onCancel,
  onSuccess,
  onRestart,
}: BacManualTransferStepProps) {
  const [statusMessage, setStatusMessage] = useState<string | undefined>(undefined);
  const [isTerminal, setIsTerminal] = useState(false);

  const [verificarPago, { isLoading: isVerificando }] = useVerificarPagoBacSimplePayMutation();

  const handleVerify = async () => {
    setStatusMessage(undefined);
    const result: any = await verificarPago(preregisterId);
    const data = result?.data?.data;

    if (result?.data?.responseStatus === "Success" && data?.estado === "Aprobado") {
      onSuccess(data.pagoId);
      return;
    }

    if (data?.estado === "Rechazado") {
      setIsTerminal(true);
      setStatusMessage(
        data.mensaje || content?.payment_rejected_message || "El pago fue rechazado",
      );
      return;
    }

    if (data?.estado === "Pendiente") {
      setStatusMessage(
        data.mensaje ||
          content?.manual_transfer_pending_message ||
          "Todavía no detectamos su transferencia SINPE Móvil, espere unos segundos e intente de nuevo",
      );
      return;
    }

    const mensaje =
      result?.error?.data?.message ||
      result?.data?.message ||
      content?.manual_transfer_verify_error ||
      "No fue posible verificar el pago";
    setStatusMessage(mensaje);
  };

  const formattedAmount = FormatterUtils.number(totalAmount.toFixed(2));

  return (
    <>
      <CountdownRow>
        <Statistic.Countdown
          title={content?.remaining_time_label || "Tiempo restante"}
          value={expiresAt}
          onFinish={onExpire}
        />
      </CountdownRow>

      <ManualTransferBox>
        <Alert
          type="info"
          showIcon
          message={content?.manual_transfer_alert_title || "Realice una transferencia SINPE Móvil"}
          description={
            <>
              {content?.manual_transfer_instruction_intro ||
                "Desde la aplicación de su banco, realice una transferencia SINPE Móvil"}
              {orgPhone ? (
                <>
                  {" "}
                  {content?.manual_transfer_instruction_phone_prefix || "al número"}{" "}
                  <strong>{orgPhone}</strong>
                </>
              ) : null}{" "}
              {content?.manual_transfer_instruction_amount_prefix || "por"}{" "}
              <strong>
                {currencySymbol}
                {formattedAmount}
              </strong>{" "}
              {content?.manual_transfer_instruction_suffix ||
                'y luego presione el botón "Ya realicé el pago" para confirmar.'}
            </>
          }
        />
      </ManualTransferBox>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 20 }}
        message={
          content?.do_not_close_warning ||
          "No cierre ni actualice esta ventana hasta que el pago se confirme como completado. Si lo hace, perderá la referencia para validarlo y deberá iniciar el proceso de nuevo."
        }
      />

      {statusMessage && (
        <Alert
          type={isTerminal ? "error" : "warning"}
          showIcon
          message={statusMessage}
          style={{ marginBottom: 20 }}
        />
      )}

      <ActionButtons>
        <Button className="cancel-btn" size="large" onClick={onCancel} disabled={isVerificando}>
          {content?.cancel_button || "Cancelar"}
        </Button>
        {isTerminal ? (
          <Button className="pay-btn" type="primary" size="large" onClick={onRestart}>
            {content?.restart_button || "Iniciar de nuevo"}
          </Button>
        ) : (
          <Button
            className="pay-btn"
            type="primary"
            size="large"
            loading={isVerificando}
            onClick={handleVerify}
          >
            {content?.manual_transfer_confirm_button || "Ya realicé el pago"}
          </Button>
        )}
      </ActionButtons>
    </>
  );
}

export default BacManualTransferStep;
